import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getToken, updateToken, useRefreshTokenMutation } from '@soulhx/client-store';
import { LoginRegister } from './users/LoginRegister.js';
import { NewVersionReload } from './common/NewVersionReload.js';
import { Home } from './home/Home.js';
import { Header } from './common/Header.js';
import { FloatingActionButtons } from './common/actionbtns/FloatingActionButtons.js';
import { SoulHXContextProvider } from '../stores/SoulHXContext.js';
import { MiniHeader } from './common/MiniHeader.js';
import { CachedColourMode, ColourModeManager } from './common/ColourModeManager.js';
const Pray = lazy(() => import('./pray/Pray.js'));
const Do = lazy(() => import('./do/Do.js'));
const Read = lazy(() => import('./read/Read.js'));
const Plans = lazy(() => import('./plans/Plans.js'));
const EditPlan = lazy(() => import('./plans/edit/EditPlan.js'));
const SizeIndicator = lazy(() => import('./common/SizeIndicator.js'));
const Users = lazy(() => import('./admin/users/UserList.js'));
const ChangePwd = lazy(() => import('./users/ChangePwd.js'));
const FourOhFour = lazy(() => import('./common/FourOhFour.js'));
const CreateAccount = lazy(() => import('./users/CreateAccount.js'));
const CustomizeActions = lazy(() => import('./do/CustomizeActions.js'));
const ConfigVis = lazy(() => import('./do/stats/ConfigVis.js'));
const MainTutorial = lazy(() => import('./main-tutorial/MainTutorial.js'));
const EditorTutorial = lazy(() => import('./editor-tutorial/EditorTutorial.js'));
const ViewPrayerItem = lazy(() => import('./pray/ViewItem.js'));
const Comms = lazy(() => import('./comms/Comms.js'));
const PassageView = lazy(() => import('./read/PassageView.js'));

const syncLogout = (event: StorageEvent) => {
  if (event.key === 'logout') {
    window.location.reload();
  }
};

const withSuspense = (element: React.ReactElement) => <Suspense fallback="...">{element}</Suspense>;

/**
 * `/passage` is rendered bare (no Header/FloatingActionButtons/SizeIndicator)
 * since it's meant to be opened in its own window/tab via `PassageLink`,
 * rather than navigated to as part of the normal app shell.
 */
const AuthenticatedRoutes = () => {
  const location = useLocation();
  const isBarePassageView = location.pathname === '/passage';
  const isPopoutView = location.search.includes('popout=true');

  if (isBarePassageView) {
    return (
      <>
        <MiniHeader />
        <Routes>
          <Route path="/passage" element={withSuspense(<PassageView />)} />
        </Routes>
      </>
    );
  }

  if (isPopoutView) {
    return (
      <>
        <MiniHeader />
        <Routes>
          <Route path="/pray" element={withSuspense(<Pray />)} />
          <Route path="/pray/:prayerId" element={withSuspense(<ViewPrayerItem />)} />
          <Route path="/read" element={withSuspense(<Read />)} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <Header />
      <FloatingActionButtons />
      <Routes>
        <Route path="/pray" element={withSuspense(<Pray />)} />
        <Route path="/pray/:prayerId" element={withSuspense(<ViewPrayerItem />)} />
        <Route path="/do" element={withSuspense(<Do />)} />
        <Route path="/do/config" element={withSuspense(<CustomizeActions />)} />
        <Route path="/do/configvis" element={withSuspense(<ConfigVis />)} />
        <Route path="/read" element={withSuspense(<Read />)} />
        <Route path="/plans" element={withSuspense(<Plans />)} />
        <Route path="/plans/edit" element={withSuspense(<EditPlan />)} />
        <Route path="/plans/edit/:planId" element={withSuspense(<EditPlan />)} />
        <Route path="/admin/users" element={withSuspense(<Users />)} />
        <Route path="/changePwd" element={withSuspense(<ChangePwd />)} />
        <Route path="/changePwd/:pwdToken" element={withSuspense(<ChangePwd />)} />
        <Route path="/tutorial" element={withSuspense(<MainTutorial />)} />
        <Route path="editortutorial" element={withSuspense(<EditorTutorial />)} />
        <Route path="/comms" element={withSuspense(<Comms />)} />
        <Route path="/comms/announcement/:itemID" element={withSuspense(<Comms fullScreenType="announcement" />)} />
        <Route path="/comms/alert/:itemID" element={withSuspense(<Comms fullScreenType="alert" />)} />
        <Route path="/comms/kb/:itemID" element={withSuspense(<Comms fullScreenType="kb" />)} />
        <Route path="/comms/bug/:itemID" element={withSuspense(<Comms fullScreenType="bug" />)} />
        <Route path="/comms/feedback/:itemID" element={withSuspense(<Comms fullScreenType="feedback" />)} />
        <Route path="/" element={<Home />} />
        <Route path="*" element={withSuspense(<FourOhFour />)} />
      </Routes>
      {withSuspense(<SizeIndicator />)}
    </>
  );
};

export const App = () => {
  const token: string = useSelector(getToken);
  const dispatch = useDispatch();
  const [refreshToken] = useRefreshTokenMutation();

  useEffect(() => {
    if (!token || token.length < 1 || token === '') return;

    const verifyUser = () => {
      if (token.length > 0) {
        refreshToken()
          .unwrap()
          .then((response) => {
            dispatch(updateToken(response.token));
          })
          .catch((err) => {
            if (err.status === 401) dispatch(updateToken(''));
          });
      }

      setTimeout(verifyUser, 5 * 60 * 1000);
    };

    verifyUser();

    window.addEventListener('storage', syncLogout);

    return () => {
      window.removeEventListener('storage', syncLogout);
    };
  }, [dispatch, refreshToken, token]);

  return (
    <>
      <NewVersionReload />
      {token === '' ? <CachedColourMode /> : <ColourModeManager />}
      {token === '' ? (
        <Suspense fallback="...">
          <BrowserRouter>
            <Routes>
              <Route path="/changePwd" element={<ChangePwd />} />
              <Route path="/changePwd/:pwdToken" element={<ChangePwd />} />
              <Route path="/createAccount" element={<CreateAccount />} />
              <Route path="/createAccount/:token" element={<CreateAccount />} />
              <Route path="*" element={<LoginRegister />} />
            </Routes>
          </BrowserRouter>
        </Suspense>
      ) : (
        <SoulHXContextProvider>
          <BrowserRouter>
            <div>
              <AuthenticatedRoutes />
            </div>
          </BrowserRouter>
        </SoulHXContextProvider>
      )}
    </>
  );
};
